import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { POPOUT_MOUNT_ATTR } from '../../../../components/PopoutWindow';
import logo from '../../../../assets/LOGOcolor.png';
import './StageLook.css';
import './LyricsWindowPage.css';

/**
 * /lyrics-window: the Lesson Dashboard's lyrics column in a window of its own
 * (2026-10-05). Anthony: "I want to be able to detach the scrolling lyric
 * control column." Since 2026-10-29 the column carries Now Playing (the
 * player pinned under the words), so this window is the music-app view;
 * the route keeps its path so links and the noindex pin do not move.
 *
 * This page renders NOTHING of its own but one empty mount node tagged with
 * the token in its URL (`?k=`). The lesson tab that opened it portals the real
 * LyricsPane into that node (components/PopoutWindow.js), so the words, the
 * follow highlight, tap-to-seek and every control are the lesson's own
 * instances, not copies: the pane here IS the pane that was in the column.
 *
 * It wears the portal shell classes (font, base tokens) but NOT the saved
 * portal theme: since 2026-10-30 it wears the Stage's look instead (Anthony:
 * "I want the lyrics window to match the aesthetic of the stage window"), the
 * brand navy ground with the faint teal bird behind the words. Like the Stage
 * it is identical in every theme. The look rides on the pane itself
 * (StageLook.css), so it matches the docked column; this page adds the
 * ground and caps the width at the player's. The page is also the SCROLL
 * CONTAINER (`.lw-mount`): LyricsWithSections finds its nearest scrolling
 * ancestor to keep the sung line in view, and a window that scrolls the
 * document has none.
 *
 * When the lesson tab goes away the opener is gone; this window then closes
 * itself rather than sitting on a second screen showing last lesson's words.
 * Staff-only by route, noindex, no portal chrome (App.js).
 */
const LyricsWindowPage = () => {
	const { search } = useLocation();
	const token = new URLSearchParams(search).get('k') || '';
	const mountRef = useRef(null);
	const [attached, setAttached] = useState(false);

	useEffect(() => {
		document.title = 'Now Playing';
	}, []);

	// The waiting line goes as soon as the lesson lands its pane here.
	useEffect(() => {
		const el = mountRef.current;
		if (!el) return undefined;
		const update = () => setAttached(el.childElementCount > 0);
		update();
		if (typeof MutationObserver !== 'function') return undefined;
		const mo = new MutationObserver(update);
		mo.observe(el, { childList: true });
		return () => mo.disconnect();
	}, []);

	// No lesson tab, no reason to exist.
	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const t = setInterval(() => {
			if (!window.opener || window.opener.closed) window.close();
		}, 2000);
		return () => clearInterval(t);
	}, []);

	return (
		<div className="cyv portal-shell lw-page">
			<div className="ld-shell lw-shell">
				<div ref={mountRef} className="lw-mount" {...{ [POPOUT_MOUNT_ATTR]: token }} />
				{!attached && (
					<div className="lw-waiting" role="status">
						<img src={logo} alt="Color Your Voice" className="lw-waiting__logo" />
						<p>
							{token
								? 'Waiting for the lesson. Now Playing appears here as soon as the Lesson Dashboard sends it.'
								: 'This window is opened from a lesson: use the detach control on the lyrics in the Lesson Dashboard.'}
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default LyricsWindowPage;
